import { logger } from './logger'

export interface RetryOptions {
  maxRetries?: number
  initialDelay?: number
  maxDelay?: number
  backoffMultiplier?: number
  retryableErrors?: string[]
  // Extra HTTP statuses (beyond RETRYABLE_HTTP_STATUSES) that this caller
  // considers retryable — e.g. analyze-grass treats OpenAI 500s as transient.
  extraRetryableStatuses?: number[]
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 4,
  extraRetryableStatuses: [],
  initialDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  backoffMultiplier: 2,
  retryableErrors: [
    'rate_limit',
    'rate_limit_exceeded',
    'too_many_requests',
    'service_unavailable',
    'timeout',
    // Per-attempt AbortController timeouts (e.g. the 90s socket guard in the
    // analyze-grass route) reject with a DOMException whose message is
    // "...operation was aborted". Treat that as retryable so a single hung
    // socket retries instead of failing the whole request, per that route's
    // documented intent.
    'aborted',
    'network',
    'ECONNRESET',
    'ETIMEDOUT',
    '429',
    '502',
    '503',
    '504',
    'application failed',
    // undici fetch() failures surface as TypeError("fetch failed") with the
    // real reason on error.cause.code (UND_ERR_SOCKET, UND_ERR_CONNECT_TIMEOUT,
    // etc). Match both the wrapper message and the cause-code prefix.
    'fetch failed',
    'und_err'
  ]
}

// HTTP statuses that are always safe to retry regardless of error message.
// Directus SDK errors sometimes surface a `status` field with the HTTP code
// but an empty `message`, so the string-based check above misses them.
const RETRYABLE_HTTP_STATUSES = new Set([429, 502, 503, 504])

// Lowercase any error field safely. `(x || '').toLowerCase()` only guards
// against falsy values — a truthy NON-string blows up. The real-world offender
// is DOMException: an AbortError (fired by our per-attempt AbortController
// timeout) has a NUMERIC `.code` (20 = ABORT_ERR), so `(20).toLowerCase()`
// threw and masked the genuine timeout as a confusing TypeError in telemetry.
function lc(value: unknown): string {
  if (typeof value === 'string') return value.toLowerCase()
  if (value == null) return ''
  return String(value).toLowerCase()
}

/**
 * Check if an error is a non-retryable quota error (permanent until billing is fixed)
 */
function isQuotaExceededError(error: any): boolean {
  const errorMessage = lc(error?.message)
  const errorCode = lc(error?.code)
  const errorType = lc(error?.error?.type ?? error?.type)
  
  // Check for insufficient_quota errors - these should NOT be retried
  return (
    errorMessage.includes('insufficient_quota') ||
    errorMessage.includes('exceeded your current quota') ||
    errorMessage.includes('quota') && errorMessage.includes('exceeded') ||
    errorCode === 'insufficient_quota' ||
    errorType === 'insufficient_quota'
  )
}

/**
 * Retry a function with exponential backoff
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  const retryableStatuses = new Set([...RETRYABLE_HTTP_STATUSES, ...opts.extraRetryableStatuses])
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error: any) {
      lastError = error

      // Errors that mark themselves non-retryable (e.g. a request deadline)
      // are rethrown before any message matching.
      if (error?.nonRetryable === true) {
        throw error
      }

      // Check if this is a quota exceeded error - these should NOT be retried
      if (isQuotaExceededError(error)) {
        logger.error('Quota exceeded error detected - not retrying', {
          error: error.message,
          errorCode: error.code,
          errorType: error?.error?.type || error?.type
        })
        throw error
      }
      
      // Check if error is retryable. Match by HTTP status first (so a
      // Directus 503 with empty message still gets retried), then fall back
      // to substring match on message + code.
      const errorMessage = lc(error?.message)
      const errorCode = lc(error?.code)
      const causeCode = lc(error?.cause?.code)
      const causeMessage = lc(error?.cause?.message)
      const errorStatus = typeof error?.status === 'number'
        ? error.status
        : typeof error?.response?.status === 'number'
          ? error.response.status
          : null
      const isRetryable =
        (errorStatus !== null && retryableStatuses.has(errorStatus)) ||
        opts.retryableErrors.some((retryable) => {
          const needle = retryable.toLowerCase()
          return (
            errorMessage.includes(needle) ||
            errorCode.includes(needle) ||
            causeCode.includes(needle) ||
            causeMessage.includes(needle)
          )
        })

      // Don't retry on last attempt or if error is not retryable
      if (attempt === opts.maxRetries || !isRetryable) {
        throw error
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(
        opts.initialDelay * Math.pow(opts.backoffMultiplier, attempt),
        opts.maxDelay
      )

      logger.warn(`Retry attempt ${attempt + 1}/${opts.maxRetries} after ${delay}ms`, {
        error: error.message,
        errorCode: error.code,
        delay
      })

      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }

  throw lastError || new Error('Retry exhausted')
}

/**
 * Check if an error is a rate limit error
 */
export function isRateLimitError(error: any): boolean {
  const message = lc(error?.message)
  const code = lc(error?.code)
  const status = error?.response?.status

  return (
    message.includes('rate_limit') ||
    message.includes('rate_limit_exceeded') ||
    message.includes('too_many_requests') ||
    code === 'rate_limit_exceeded' ||
    status === 429
  )
}




